import type { FeasibilityLimitRow, FeasibilityResponse } from "@/api/schemas";
import { asLimitRow } from "@/features/feasibility/FeasibilityResult";
import type { FeasibilityValues } from "@/features/projects/useProjectStore";

/**
 * Paket 2 — čiste funkcije oko sesije verdikta (revizija P1-1, P1-5).
 *
 * Sve je bez rendera i bez mreže: potpis parametara (je li prikazani
 * verdikt još vezan uz ono što korisnik trenutno gleda), reducirani
 * payload za PDF (persist bez ogromnog evidence_bundlea) i deterministički
 * lookup limita za instant odgovor u razgovoru.
 */

/** Ono što `export_feasibility_pdf` stvarno čita — ostatak odgovora
 *  motora (evidence_bundle, project_requirements) je prevelik za
 *  localStorage i PDF-u ne treba. */
export interface FeasibilityExportPayload {
  outcome_class: string;
  elapsed_seconds?: number;
  municipality_name?: string | null;
  municipality_code?: string;
  plan_version?: string | null;
  memo_text: string;
  parameter_validation: unknown[];
  llm_output: {
    blockers: unknown[];
    conditions_and_limits: unknown[];
    key_findings: unknown[];
  };
}

export function toExportPayload(res: FeasibilityResponse): FeasibilityExportPayload {
  return {
    outcome_class: res.outcome_class,
    elapsed_seconds: res.elapsed_seconds,
    municipality_name: res.municipality_name ?? null,
    municipality_code: res.municipality_code ?? undefined,
    plan_version: res.plan_version ?? null,
    memo_text: res.memo_text,
    parameter_validation: res.parameter_validation,
    llm_output: {
      blockers: [...res.pre_llm_blockers, ...res.llm_output.blockers],
      conditions_and_limits: res.llm_output.conditions_and_limits,
      key_findings: res.llm_output.key_findings,
    },
  };
}

/**
 * Potpis parametara na kojima verdikt vrijedi. Uključuje i k.č./k.o. iz
 * razgovora: motor ih ne prima (nisu polja forme), ali korisnik verdikt
 * čita kao "za OVU česticu" — promjena čestice mora ga proglasiti
 * zastarjelim (revizija P1-5).
 */
export function verdictSignature(
  values: Pick<
    FeasibilityValues,
    | "building_type"
    | "building_subtype"
    | "work_type"
    | "municipality_code"
    | "designation"
    | "in_city_center"
  >,
  parcel?: { kc?: string | null; ko?: string | null } | null,
): string {
  return [
    values.building_type ?? "",
    values.building_subtype ?? "",
    values.work_type ?? "",
    values.municipality_code ?? "",
    values.designation ?? "",
    values.in_city_center ? "1" : "0",
    parcel?.kc ?? "",
    parcel?.ko ?? "",
  ].join("|");
}

/**
 * Most u projekt (revizija P1-6): ono što je razgovor već pročitao iz
 * službenog katastra ne smije se tražiti ponovno ručno.
 *
 * Vraća SAMO polja koja stvarno postoje (fill-empty duh ostalih mappera) —
 * ništa se ne izmišlja; površina ide samo ako ju je G0 vratio.
 */
export function chatToProjectData(
  parsed?: {
    kc?: string | null;
    ko?: string | null;
    municipality_name?: string | null;
  } | null,
  parcelAreaM2?: number | null,
  naselje?: string | null,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (parsed?.kc) out.katastarska_cestica = parsed.kc;
  if (parsed?.ko) out.katastarski_opcina = parsed.ko;
  if (typeof parcelAreaM2 === "number" && parcelAreaM2 > 0) {
    out.cestica_povrsina_m2 = parcelAreaM2;
  }
  // Lokacija zahvata: naselje iz zone ako ga plan nosi, inače grad/općina.
  const location = (naselje || "").trim() || (parsed?.municipality_name || "").trim();
  if (location) out.project_location = location;
  return out;
}

/** Ime projekta iz onoga što razgovor zna — čitljivije od "Novi projekt". */
export function projectNameFromChat(parsed?: { kc?: string | null; ko?: string | null } | null): string | null {
  if (!parsed?.kc) return null;
  return parsed.ko ? `k.č. ${parsed.kc}, k.o. ${parsed.ko}` : `k.č. ${parsed.kc}`;
}

/**
 * Deterministički lookup limita za instant odgovor na follow-up.
 *
 * NE generira tekst: vraća stavku koja VEĆ postoji u izračunatom verdiktu
 * (s njenim citatom). Nema pogotka → null → chat šuti i čeka motor
 * (citiraj-ili-šuti).
 */
const LIMIT_KEYWORDS: Array<{ probe: RegExp; matches: RegExp }> = [
  { probe: /\betaž|\bkatn|\bnadzemn|\bpo\+p|\bp\+\d/i, matches: /etaž/i },
  { probe: /\bkig\b|izgrađenost|izgradenost/i, matches: /\bkig\b|izgrađenost/i },
  { probe: /\bkisn\b|iskorišten|iskorišten/i, matches: /\bkisn\b|iskorišten/i },
  { probe: /\bvisin|\bv=|\bmetara\b/i, matches: /visin/i },
  { probe: /\bpovršin\w*\s+čestic|\bveličin\w*\s+čestic|\bm2\b|\bm²/i, matches: /površina čestice|min\.\s*površina/i },
  { probe: /\budaljen|\bmeđ[ae]\b|\bregulacijsk/i, matches: /udaljenost/i },
  { probe: /\bparkir|\bparking|\bgaraž/i, matches: /parking|parkir/i },
];

/**
 * Jedna rečenica sinteze preko zona (revizija P1-3).
 *
 * Na pitanje "što mogu graditi na OVOJ čestici" verdikt za jednu zonu nije
 * cijeli odgovor: korisnik mora znati da je gradivi dio čestice možda samo
 * mali postotak. Sve je izračunato iz podataka koje G0 već vraća (udjeli +
 * površina čestice) — ništa se ne procjenjuje.
 */
export function zoneSynthesis(
  zones: Array<{ oznaka: string; udio_pct: number; primarna?: string | null }>,
  selected: string | null | undefined,
  parcelAreaM2?: number | null,
): string | null {
  if (!zones || zones.length < 2) return null;
  const sel = zones.find((z) => z.oznaka === selected) ?? zones[0];
  const others = zones.filter((z) => z.oznaka !== sel.oznaka);
  const pct = sel.udio_pct;
  const area =
    parcelAreaM2 && pct
      ? ` ≈ ${Math.round((parcelAreaM2 * pct) / 100).toLocaleString("hr-HR")} m²`
      : "";
  const isBuildingArea = (sel.primarna ?? "").trim().toLowerCase() === "naselje";
  const what = isBuildingArea ? "građevinsko područje naselja" : "zona";
  const rest = others
    .map((z) => `${z.oznaka} ${z.udio_pct} %`)
    .join(", ");
  return (
    `Provjera vrijedi za ${what} ${sel.oznaka} — ${pct} % čestice${area}. ` +
    `Ostatak čestice: ${rest}. Za drugu zonu klikni njen chip.`
  );
}

export function matchLimitsForQuestion(
  question: string,
  conditions: Array<string | FeasibilityLimitRow>,
): FeasibilityLimitRow[] {
  const q = question ?? "";
  const topic = LIMIT_KEYWORDS.find((k) => k.probe.test(q));
  if (!topic) return [];
  const rows = conditions.map(asLimitRow).filter((r) => r.name && r.value);
  const hits = rows.filter((r) => topic.matches.test(r.name));
  // Isti parametar se ponavlja po kontekstu primjene (neposredna/posredna) —
  // pokaži oba, ali nikad cijelu tablicu.
  return hits.slice(0, 3);
}
